import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Icon } from "@iconify/react/dist/iconify.js";
import MasterLayout from "../../masterLayout/MasterLayout";
import Breadcrumb from "../Breadcrumb";
import Loader from "../child/Loder";
import DengerAlert from "../child/Alert/DengerAlert";
import {
  fetchAdditionalBottleServiceTransactions,
  fetchPromoterRequests,
} from "../../services/promoterRequestService";
import { getUserById } from "../../services/userService";
import { formatApiDate } from "../../utils/dateHelper";

const AdditionalBottleServiceList = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [hostId, setHostId] = useState("");
  const [clubId, setClubId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [venueOptions, setVenueOptions] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [approvedRequests, setApprovedRequests] = useState([]);

  const toId = (value) => {
    if (value == null || value === "") return "";
    if (typeof value === "object") {
      return String(value._id || value.id || "").trim();
    }
    return String(value).trim();
  };

  const getRowHostId = (row) =>
    toId(row?.hostId) ||
    toId(row?.host) ||
    toId(row?.hostDetail?.hostId) ||
    toId(row?.hostDetail) ||
    toId(row?.userId);

  const getRowPromoterId = (row) =>
    toId(row?.promoterId) ||
    toId(row?.promoter) ||
    toId(row?.promoterDetail) ||
    toId(row?.promoterDetails);

  const getRowClubId = (row) =>
    toId(row?.clubId) ||
    toId(row?.eventId) ||
    toId(row?.club) ||
    toId(row?.event) ||
    toId(row?.clubDetail?.clubId);

  const getRowClubName = (row) =>
    String(
      row?.clubName ||
        row?.eventName ||
        row?.club?.name ||
        row?.event?.name ||
        row?.clubDetail?.clubName ||
        row?.name ||
        row?.title ||
        ""
    ).trim();

  const isInvalidBottleServiceLabel = (label) => {
    const normalized = String(label || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
    return (
      !normalized ||
      normalized === "-" ||
      normalized === "—" ||
      normalized === "–" ||
      normalized === "_" ||
      normalized === "n/a" ||
      normalized === "null" ||
      normalized === "undefined" ||
      normalized === "event"
    );
  };

  const isBottleServiceItem = (row, label) => {
    const text = String(label || "").toLowerCase();
    const type = String(
      row?.type || row?.listingType || row?.eventType || row?.category || ""
    ).toLowerCase();
    return (
      text.includes("bottle") ||
      type.includes("bottle") ||
      Boolean(row?.bottleService)
    );
  };

  const fetchApprovedVenues = async () => {
    if (!id) return;
    try {
      setFiltersLoading(true);

      const [res, userRes] = await Promise.all([
        fetchPromoterRequests({
          page: 1,
          limit: 9999,
          status: "approved",
          promoterId: id,
          type: "host",
        }),
        getUserById(id).catch(() => null),
      ]);

      const promoterName = String(
        userRes?.data?.user?.fullName ||
          userRes?.data?.data?.fullName ||
          userRes?.data?.fullName ||
          ""
      )
        .trim()
        .toLowerCase();

      const list = Array.isArray(res?.data?.data)
        ? res.data.data
        : Array.isArray(res?.data?.result)
          ? res.data.result
          : [];

      const filtered = list.filter((row) => {
        const rowStatus = String(row?.status || "").toLowerCase();
        if (rowStatus && rowStatus !== "approved") return false;

        const rowType = String(row?.type || row?.requestType || "")
          .toLowerCase()
          .replace(/[\s_-]/g, "");
        if (rowType === "event") return false;

        const rowPromoterId = getRowPromoterId(row);
        if (rowPromoterId) return rowPromoterId === String(id);

        if (!promoterName) return false;
        const rowPromoterName = String(
          row?.promoterName ||
            row?.promoter?.fullName ||
            row?.promoterDetail?.fullName ||
            ""
        )
          .trim()
          .toLowerCase();
        return Boolean(rowPromoterName) && rowPromoterName === promoterName;
      });

      setApprovedRequests(filtered);

      const uniqueVenues = new Map();
      filtered.forEach((row) => {
        const value = getRowHostId(row);
        if (!value) return;
        const label =
          row?.businessName ||
          row?.hostBusinessName ||
          row?.hostDetail?.businessName ||
          row?.hostName ||
          row?.host?.businessName ||
          row?.host?.fullName ||
          value;
        if (!uniqueVenues.has(value)) {
          uniqueVenues.set(value, {
            value,
            label: String(label),
          });
        }
      });

      setVenueOptions(
        Array.from(uniqueVenues.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      );
    } catch (error) {
      console.error("Approved venue fetch error:", error);
      setApprovedRequests([]);
      setVenueOptions([]);
    } finally {
      setFiltersLoading(false);
    }
  };

  const buildUniqueBottleServiceOptions = (list) => {
    const byId = new Map();
    const byLabel = new Map();

    list.forEach((row) => {
      const value = getRowClubId(row) || String(row?._id || row?.id || "");
      const label = getRowClubName(row);
      if (!value || isInvalidBottleServiceLabel(label)) return;

      const normalizedLabel = label.toLowerCase().replace(/\s+/g, " ").trim();
      if (byId.has(value) || byLabel.has(normalizedLabel)) return;

      byId.set(value, true);
      byLabel.set(normalizedLabel, {
        value,
        label,
        isBottle: isBottleServiceItem(row, label),
      });
    });

    const allOptions = Array.from(byLabel.values());
    const bottleOnly = allOptions.filter((opt) => opt.isBottle);
    return (bottleOnly.length > 0 ? bottleOnly : allOptions)
      .map(({ value, label }) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const fetchBottleServicesByHost = async (selectedHostId) => {
    if (!selectedHostId) {
      setEventOptions([]);
      return;
    }

    try {
      const optionMap = new Map();

      const addOptions = (list) => {
        buildUniqueBottleServiceOptions(list).forEach((opt) => {
          if (!optionMap.has(opt.value)) {
            optionMap.set(opt.value, opt);
          }
        });
      };

      // Only bottle services from this promoter's approved venues
      addOptions(
        approvedRequests.filter(
          (row) => getRowHostId(row) === String(selectedHostId)
        )
      );

      const txnRes = await fetchAdditionalBottleServiceTransactions(id, {
        page: 1,
        limit: 9999,
        hostId: selectedHostId,
        clubId: "",
        status: "",
        saleDate: "",
        startDate: "",
        endDate: "",
        search: "",
      });
      addOptions(txnRes?.data?.data || []);

      setEventOptions(
        Array.from(optionMap.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      );
    } catch (error) {
      console.error("Bottle service fetch error:", error);
      setEventOptions([]);
    }
  };

  const fetchList = async () => {
    if (!id) return;

    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";

    if (
      formattedStartDate &&
      formattedEndDate &&
      moment(formattedStartDate).isAfter(moment(formattedEndDate))
    ) {
      setErrorMessage("Start date cannot be greater than end date");
      setTimeout(() => setErrorMessage(""), 3000);
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      const response = await fetchAdditionalBottleServiceTransactions(id, {
        page: currentPage,
        limit: itemsPerPage,
        hostId: hostId || "",
        clubId: clubId || "",
        status: statusFilter || "",
        transactionStatus: statusFilter || "",
        saleDate: "",
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        search: searchQuery || "",
      });

      if (response?.data?.status) {
        setItems(response?.data?.data || []);
        setTotalItems(response?.data?.total || 0);
        setTotalPages(response?.data?.totalPages || 1);
      } else {
        setItems([]);
        setTotalItems(0);
        setTotalPages(1);
        setErrorMessage(
          response?.data?.message ||
            "Unable to fetch additional bottle service transactions."
        );
      }
    } catch (error) {
      console.error("Additional bottle service list error:", error);
      setItems([]);
      setTotalItems(0);
      setTotalPages(1);
      setErrorMessage(
        error?.response?.data?.message ||
          "Something went wrong while fetching transactions."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovedVenues();
  }, [id]);

  useEffect(() => {
    fetchBottleServicesByHost(hostId);
  }, [hostId, approvedRequests]);

  useEffect(() => {
    fetchList();
  }, [
    id,
    currentPage,
    itemsPerPage,
    searchQuery,
    statusFilter,
    hostId,
    clubId,
    startDate,
    endDate,
  ]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  const getPaginationRange = () => {
    if (totalPages <= 5) {
      return Array.from({ length: Math.max(totalPages, 0) }, (_, i) => i + 1);
    }
    if (currentPage <= 3) return [1, 2, 3, 4, 5];
    if (currentPage >= totalPages - 2) {
      return [
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    return [
      currentPage - 2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      currentPage + 2,
    ];
  };

  const formatSaleDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const hasTime =
      String(dateStr).includes("T") &&
      !String(dateStr).endsWith("T00:00:00.000Z");
    if (hasTime) {
      return moment(dateStr).format("DD-MM-YYYY HH:mm");
    }
    return formatApiDate(dateStr);
  };

  const formatStatus = (value) => {
    if (!value) return "N/A";
    const normalized = String(value).toLowerCase();
    if (normalized === "paid") return "Paid";
    if (normalized === "pending") return "Pending";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  const getStatusClass = (status) => {
    switch (String(status || "").toLowerCase()) {
      case "paid":
        return "bg-success-focus text-success-600 border border-success-main";
      case "pending":
        return "bg-warning-focus text-warning-600 border border-warning-main";
      default:
        return "bg-neutral-200 text-secondary-light border";
    }
  };

  const formatBottleServiceName = (name) => {
    if (!name) return "N/A";
    return String(name)
      .replace(/\s*[-–—]\s*/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  };

  if (loading && items.length === 0) {
    return (
      <MasterLayout>
        <Breadcrumb
          title2="Promoter Management"
          link2="/promoter"
          title3="View Promoter"
          link3={`/promoter/view-promoter/${id}`}
          title="Additional Bottle Services"
        />
        <Loader />
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <Breadcrumb
        title2="Promoter Management"
        link2="/promoter"
        title3="View Promoter"
        link3={`/promoter/view-promoter/${id}`}
        title="Additional Bottle Services"
      />

      <div className="card h-100 p-0 radius-12">
        {errorMessage && (
          <DengerAlert
            message={errorMessage}
            onClose={() => setErrorMessage("")}
          />
        )}

        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span className="text-md fw-medium text-secondary-light mb-0">
              Show
            </span>
            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10 items per page</option>
              <option value="20">20 items per page</option>
              <option value="50">50 items per page</option>
              <option value="100">100 items per page</option>
            </select>

            <form
              className="navbar-search"
              style={{ position: "relative" }}
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="text"
                className="bg-base h-40-px w-auto pe-40"
                name="search"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  paddingRight: searchQuery ? "5rem" : "2.5rem",
                }}
              />
              <Icon
                icon="ion:search-outline"
                className="icon"
                style={{
                  position: "absolute",
                  right: searchQuery ? "3.25rem" : "1rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#999",
                  pointerEvents: "none",
                }}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "30%",
                    transform: "translateY(-50%)",
                    fontSize: "1.25rem",
                    color: "#000",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <Icon icon="ic:round-close" />
                </button>
              )}
            </form>

            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={hostId}
              disabled={filtersLoading}
              onChange={(e) => {
                setHostId(e.target.value);
                setClubId("");
                setCurrentPage(1);
              }}
            >
              <option value="">All Venue</option>
              {venueOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={clubId}
              disabled={!hostId}
              onChange={(e) => {
                setClubId(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Bottle Services</option>
              {eventOptions
                .filter((opt) => !isInvalidBottleServiceLabel(opt.label))
                .map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {formatBottleServiceName(opt.label)}
                  </option>
                ))}
            </select>

            <select
              className="form-select form-select-sm w-auto ps-12 py-6"
              style={{
                fontSize: "16px",
                borderRadius: "7px",
                backgroundPositionX: "unset",
                backgroundPosition: "right 0.75rem center",
              }}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>

            <div style={{ position: "relative", maxWidth: "200px" }}>
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  setStartDate(date);
                  if (
                    date &&
                    endDate &&
                    moment(date).isAfter(moment(endDate), "day")
                  ) {
                    setEndDate(null);
                  }
                  setCurrentPage(1);
                }}
                preventOpenOnFocus={false}
                onKeyDown={(e) => e.preventDefault()}
                className="form-control no-caret"
                placeholderText="Start Date"
                dateFormat="yyyy-MM-dd"
              />
              {startDate && (
                <button
                  type="button"
                  onClick={() => {
                    setStartDate(null);
                    setCurrentPage(1);
                  }}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    fontSize: "1.25rem",
                    color: "#000",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <Icon icon="ic:round-close" />
                </button>
              )}
            </div>

            <div style={{ position: "relative", maxWidth: "200px" }}>
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  setEndDate(date);
                  setCurrentPage(1);
                }}
                minDate={startDate || undefined}
                preventOpenOnFocus={false}
                onKeyDown={(e) => e.preventDefault()}
                className="form-control no-caret"
                placeholderText="End Date"
                dateFormat="yyyy-MM-dd"
              />
              {endDate && (
                <button
                  type="button"
                  onClick={() => {
                    setEndDate(null);
                    setCurrentPage(1);
                  }}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    fontSize: "1.25rem",
                    color: "#000",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <Icon icon="ic:round-close" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Venue Name</th>
                  <th>Bottle Services</th>
                  <th>Promoter Commission</th>
                    <th>Sale Amount</th>
                  <th>Sale Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <tr key={item._id || index}>
                      <td>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td>
                        {item.businessName ||
                          item.hostDetail?.businessName ||
                          "N/A"}
                      </td>
                      <td>
                        {formatBottleServiceName(
                          item.clubName || item.clubDetail?.clubName
                        )}
                      </td>
                      <td className="fw-semibold">
                        $
                        {Number(
                          item.payoutAmount ?? item.commissionAmount ?? 0
                        ).toLocaleString()}
                      </td>
                      <td className="fw-semibold">
                        $
                        {Number(
                          item.totalBaseAmount ?? item.totalBaseAmount ?? 0
                        ).toLocaleString()}
                      </td>
                      <td>{formatSaleDate(item.saleDate)}</td>
                      <td>
                        <span
                          className={`px-24 py-4 radius-4 fw-medium text-sm ${getStatusClass(
                            item.transactionStatus || item.payoutStatus
                          )}`}
                        >
                          {formatStatus(
                            item.transactionStatus || item.payoutStatus
                          )}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          title="View"
                          className="bg-info-focus text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          onClick={() =>
                            navigate(
                              `/promoter/additional-bottle-service/view/${id}/${item._id}`,
                              { state: { item } }
                            )
                          }
                        >
                          <Icon icon="majesticons:eye-line" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      {loading
                        ? "Loading additional bottle services..."
                        : "No additional bottle service transactions found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              {totalItems === 0 ? (
                <>No entry to show</>
              ) : (
                <>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems} entries
                </>
              )}
            </span>

            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  type="button"
                  disabled={currentPage === 1 || loading}
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <Icon icon="ep:d-arrow-left" />
                </button>
              </li>

              {getPaginationRange().map((pageNum) => (
                <li key={pageNum} className="page-item">
                  <button
                    type="button"
                    disabled={loading}
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                      currentPage === pageNum
                        ? "bg-primary-600 text-white"
                        : "bg-neutral-200"
                    }`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              ))}

              <li
                className={`page-item ${
                  currentPage === totalPages || totalPages === 0
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  type="button"
                  disabled={
                    currentPage === totalPages || totalPages === 0 || loading
                  }
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <Icon icon="ep:d-arrow-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default AdditionalBottleServiceList;
